/*
  Device form-factor — the ONE place the client asks "is this a touch phone?".

  The mobile adaptation pattern (keep it this small):
  - Pure sizing → Tailwind's built-in `pointer-coarse:` variant (bigger touch
    targets), directly in existing markup. No JS, no forked components.
  - Phone-only layout shifts → the `phone:` custom variant (see app.css),
    driven by the `.phone` root class set here.
  - Mobile-only COMPONENTS (virtual joystick, rotate overlay) and behavior
    branches → `{#if isPhone()}` on this store. Nothing else forks.

  "Phone" = coarse pointer AND a viewport whose smaller side is under 600px.
  Tablets deliberately get the desktop layout + coarse-pointer sizing only.
  Dev: `?phone=1` forces phone mode for desktop preview/testing.
*/

const FORCED =
  typeof location !== "undefined" && new URLSearchParams(location.search).get("phone") === "1";

const mq = (q: string) => (typeof matchMedia !== "undefined" ? matchMedia(q) : null);
const coarse = mq("(pointer: coarse)");
const portrait = mq("(orientation: portrait)");
const small = mq("(max-width: 599px), (max-height: 599px)");

export const device = $state({
  touch: FORCED || (coarse?.matches ?? false),
  portrait: portrait?.matches ?? false,
  small: FORCED || (small?.matches ?? false),
});

export function isPhone(): boolean {
  return device.touch && device.small;
}

function syncRootClasses(): void {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("touch", device.touch);
  document.documentElement.classList.toggle("phone", isPhone());
}
coarse?.addEventListener("change", (e) => { device.touch = FORCED || e.matches; syncRootClasses(); });
portrait?.addEventListener("change", (e) => { device.portrait = e.matches; });
small?.addEventListener("change", (e) => { device.small = FORCED || e.matches; syncRootClasses(); });
syncRootClasses();

/*
  Best-effort landscape enforcement. `screen.orientation.lock()` only works in
  fullscreen and only on some platforms (Chrome/Android yes, iOS Safari no) —
  so this is called from a real user gesture, and the RotateOverlay covers the
  platforms that refuse (physically rotating the phone).
*/
export async function lockLandscape(): Promise<void> {
  try {
    if (!document.fullscreenElement) await document.documentElement.requestFullscreen({ navigationUI: "hide" });
    await (screen.orientation as { lock?: (o: string) => Promise<void> }).lock?.("landscape");
  } catch {
    /* iOS / desktop: the rotate overlay + CSS carry it from here */
  }
}
